import { Schema, Types, model, models, Model, ObjectId } from "mongoose";
import { stringifyFields } from "based-shared/utils/stringify-fields";

export interface IRouter {
  _id: ObjectId;
  baseTokenForPair: string;
  router: string;
  positionManager: string;
  version: number;
  fee: Schema.Types.Decimal128;
  per: Schema.Types.Decimal128;
  pair: string;
  lpAmount: Schema.Types.Decimal128;
  isLPBurn: boolean;
  lpLockUp: number;
}

export interface IMemeTokenMetaInfo {
  seo: string;
  logo: string;
  banner: string;
  twitter: string;
  telegram: string;
  website: string;
  protocol: string;
  subOwner: string;
  seed: string;
  description: string;
}

export interface ITokenInfo {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: Schema.Types.Decimal128;
}

export interface IMemeToken extends Document {
  id: string;
  _id?: ObjectId;
  __v?: number;
  chainId: number;
  address: string;
  packageIndex: number;
  owner: string;
  ownerName: string;
  volume: Schema.Types.Decimal128;
  reserveToken: Schema.Types.Decimal128;
  reserveETH: Schema.Types.Decimal128;
  currentPrice: Schema.Types.Decimal128;
  currentUSDPrice: bigint;

  initialETH: Schema.Types.Decimal128;
  initialToken: Schema.Types.Decimal128;
  initialPrice: Schema.Types.Decimal128;
  initialBuyAmount: Schema.Types.Decimal128;

  isListed: boolean;
  isListedAt: number;
  isCancelled: boolean;
  isCancelledAt: number;
  isHot: boolean;
  isHardCapReached: boolean;
  isHardCapReachedAt: number;
  isUniswapV3Compatible: boolean;
  buyRefFee: Schema.Types.Decimal128;
  sellFee: Schema.Types.Decimal128;
  buyFee: Schema.Types.Decimal128;
  hardCap: Schema.Types.Decimal128;
  softCap: Schema.Types.Decimal128;

  initialMarketCap: Schema.Types.Decimal128;
  currentMarketCap: Schema.Types.Decimal128;

  liqHardCap: Schema.Types.Decimal128;
  liqSoftCap: Schema.Types.Decimal128;

  softPercent: Schema.Types.Decimal128;
  hardPercent: Schema.Types.Decimal128;

  maxAllocationPerUser: Schema.Types.Decimal128;
  routers: IRouter[];
  metaUri: string;
  metaInfo: IMemeTokenMetaInfo;
  metaInfoUpdatedAt: number;
  isWhitelist: boolean;
  whitelistId: string;
  whitelist: string[];
  whitelistMerkleRoot: string;
  maxAllocationPerWhitelistedUser: Schema.Types.Decimal128;
  whitelistUpdatedAt: number;
  tokenInfo: ITokenInfo;
  isXSale: boolean;
  keyForXSale: string;

  startTime: Date;
  endTime: Date;

  likedBy: {
    network: string;
    address: string;
  }[];

  txCount: number;
  cntComments: number;
}

const memeTokenSchema = new Schema<IMemeToken>(
  {
    chainId: { type: Number, required: true },
    address: { type: String, default: "" },
    packageIndex: {
      type: Number,
      set: (value: string) => Number(value),
    },
    owner: { type: String, default: "" },
    ownerName: { type: String, default: "" },
    volume: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    reserveToken: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    reserveETH: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    currentPrice: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    initialETH: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    initialToken: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    initialPrice: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    initialBuyAmount: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    isListed: { type: Boolean, default: false },
    isListedAt: {
      type: Number,
      set: (value: string) => Number(value),
      default: Date.now,
    },
    isCancelled: { type: Boolean, default: false },
    isCancelledAt: {
      type: Number,
      set: (value: string) => Number(value),
      default: Date.now,
    },
    isHot: {
      type: Boolean,
      default: false,
    },
    isHardCapReached: { type: Boolean, default: false },
    isHardCapReachedAt: {
      type: Number,
      set: (value: string) => Number(value),
      default: Date.now,
    },
    isXSale: { type: Boolean, default: false },
    keyForXSale: { type: String, default: "" },
    isUniswapV3Compatible: { type: Boolean, default: false },
    buyRefFee: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    sellFee: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    buyFee: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    hardCap: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    softCap: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    initialMarketCap: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    currentMarketCap: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    liqHardCap: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    liqSoftCap: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    maxAllocationPerUser: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    routers: [
      {
        baseTokenForPair: { type: String },
        router: { type: String },
        positionManager: { type: String },
        version: {
          type: Number,
          set: (value: string) => Number(value),
        },
        fee: {
          type: Schema.Types.Decimal128,
          default: new Types.Decimal128("0"),
        },
        per: {
          type: Schema.Types.Decimal128,
          default: new Types.Decimal128("0"),
        },
        pair: { type: String },
        lpAmount: {
          type: Schema.Types.Decimal128,
          default: new Types.Decimal128("0"),
        },
        isLPBurn: { type: Boolean, default: true },
        lpLockUp: {
          type: Number,
          set: (value: string) => Number(value),
        },
      },
    ],
    metaUri: { type: String, default: "" },
    metaInfo: {
      seo: { type: String, default: "" },
      logo: { type: String, default: "" },
      banner: { type: String, default: "" },
      twitter: { type: String, default: "" },
      telegram: { type: String, default: "" },
      website: { type: String, default: "" },
      protocol: { type: String, default: "" },
      subOwner: { type: String, default: "" },
      seed: { type: String, default: "" },
      description: { type: String, default: "" },
    },
    metaInfoUpdatedAt: {
      type: Number,
      set: (value: string) => Number(value),
      default: Date.now,
    },
    isWhitelist: { type: Boolean, default: false },
    whitelistId: { type: String, default: "" },
    whitelist: [{ type: String }],
    whitelistMerkleRoot: { type: String, default: "" },
    maxAllocationPerWhitelistedUser: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    whitelistUpdatedAt: {
      type: Number,
      set: (value: string) => Number(value),
      default: Date.now,
    },
    tokenInfo: {
      name: { type: String, default: "" },
      symbol: { type: String, default: "" },
      decimals: {
        type: Number,
        set: (value: string) => Number(value),
      },
      totalSupply: {
        type: Schema.Types.Decimal128,
        default: new Types.Decimal128("0"),
      },
    },
    softPercent: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },
    hardPercent: {
      type: Schema.Types.Decimal128,
      default: new Types.Decimal128("0"),
    },

    startTime: {
      type: Date,
      default: Date.now,
    },
    endTime: {
      type: Date,
      default: Date.now,
    },

    txCount: {
      type: Number,
      default: 0,
    },

    likedBy: [
      {
        network: { type: String },
        address: { type: String },
      },
    ],
    cntComments: {
      type: Number,
      default: 0,
    },
  },
  {
    timestamps: true,
    toObject: {
      getters: true,
      versionKey: false,
      transform: (doc, ret: IMemeToken) => {
        if (ret._id) {
          ret.id = ret._id.toString();
          delete ret._id;
        }
        delete ret.__v;
        return stringifyFields(ret);
      },
    },
  }
);

memeTokenSchema.index({ chainId: 1, address: 1 }, { unique: true });
memeTokenSchema.index({ chainId: 1, owner: 1 });
memeTokenSchema.index({ chainId: 1, ownerName: 1 });
memeTokenSchema.index({ chainId: 1, isListed: 1 });
memeTokenSchema.index({ chainId: 1, isHot: 1 });
memeTokenSchema.index({ chainId: 1, isHardCapReached: 1 });
memeTokenSchema.index({ chainId: 1, isCancelled: 1 });
memeTokenSchema.index({ chainId: 1, isWhitelist: 1 });
memeTokenSchema.index({ chainId: 1, "metaInfo.seo": 1 });
memeTokenSchema.index({ chainId: 1, "metaInfo.twitter": 1 });
memeTokenSchema.index({ chainId: 1, "metaInfo.telegram": 1 });
memeTokenSchema.index({ chainId: 1, "metaInfo.website": 1 });
memeTokenSchema.index({ chainId: 1, "metaInfo.protocol": 1 });
memeTokenSchema.index({ chainId: 1, "metaInfo.subOwner": 1 });
memeTokenSchema.index({ chainId: 1, "metaInfo.seed": 1 });
memeTokenSchema.index({ chainId: 1, "metaInfo.description": 1 });
memeTokenSchema.index({ chainId: 1, "tokenInfo.name": 1 });
memeTokenSchema.index({ chainId: 1, "tokenInfo.symbol": 1 });

export const MemeTokenModel: Model<IMemeToken> =
  models?.MemeToken || model<IMemeToken>("MemeToken", memeTokenSchema);
